"use client";
import {
  __spreadProps,
  __spreadValues
} from "../../../chunk-MMUBH76A.js";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { useAddFunds } from "./AddFundsContext.js";
import { CpslButton, CpslIcon, CpslInput, CpslRow, CpslText } from "@getpara/react-components";
import { AssetIcon, HeaderSelect, HeaderSelectContainer, HeaderSelectItem, NetworkIcon } from "../common.js";
import { getAssetCode, getNetworkName } from "../../constants/constants.js";
import { EnabledFlow, getOnRampNetworks, OnRampPurchaseType } from "@getpara/web-sdk";
import { OnRampStep, useModalStore, useThemeStore } from "../../stores/index.js";
import styled from "styled-components";
import { contentMotionProps, NoProviders } from "./common.js";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import { AddFundsAsset } from "./AddFundsAsset.js";
function NetworkPill({
  network,
  gap = "4px",
  slot,
  start,
  fix = false
}) {
  return /* @__PURE__ */ jsxs(CpslRow, __spreadProps(__spreadValues({ gap }, slot ? { slot } : {}), { children: [
    start,
    /* @__PURE__ */ jsx(NetworkIcon, { network, size: "24px" }),
    /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "contrast", style: fix ? { marginRight: "8px" } : void 0, children: getNetworkName(network) })
  ] }));
}
function AssetPill({ asset, gap = "4px", slot, fix }) {
  if (!asset) {
    return null;
  }
  return /* @__PURE__ */ jsxs(CpslRow, __spreadProps(__spreadValues({ gap }, slot ? { slot } : {}), { children: [
    /* @__PURE__ */ jsx(AssetIcon, { asset, size: "24px" }),
    /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "contrast", style: fix ? { marginRight: "8px" } : void 0, children: getAssetCode(asset) })
  ] }));
}
const NetworkLabel = /* @__PURE__ */ jsx(CpslText, { variant: "bodyXS", color: "secondary", style: { margin: "0 8px" }, children: "Network" });
function AddFundsSettings() {
  const isPresent = useIsPresent();
  const hideWallets = useThemeStore((state) => state.hideWallets);
  const setOnRampStep = useModalStore((state) => state.setOnRampStep);
  const {
    assets,
    networks,
    asset,
    setAsset,
    network,
    setNetwork,
    fiatQuantity,
    setFiatQuantity,
    onRampConfig,
    activeWallet,
    isProviderAllowed,
    tab
  } = useAddFunds();
  const [value, setValue] = useState(parseFloat(fiatQuantity).toFixed(2) || "25.00");
  const narrowedNetworks = useMemo(() => {
    return !asset ? networks : getOnRampNetworks(onRampConfig.assetInfo, {
      walletType: activeWallet.type,
      allowed: onRampConfig.allowedAssets ? Object.entries(onRampConfig.allowedAssets).filter(([_, value2]) => value2 === true || value2.includes(asset)).map(([key]) => key) : void 0,
      assets: [asset],
      providers: onRampConfig.providers,
      action: tab === EnabledFlow.BUY ? OnRampPurchaseType.BUY : OnRampPurchaseType.SELL
    });
  }, [networks, network, asset, activeWallet.type, onRampConfig.providers, tab]);
  const content = useMemo(() => {
    if (!!asset && Object.values(isProviderAllowed).every((v) => !v) || assets.length === 0) {
      return /* @__PURE__ */ jsx(Container, __spreadProps(__spreadValues({}, contentMotionProps), { isPresent, children: /* @__PURE__ */ jsxs(NoProviders, { isHidden: Object.values(isProviderAllowed).some((v) => !!v), variant: "bodyM", children: [
        "No providers are available for this ",
        hideWallets ? "account" : "wallet"
      ] }) }), "noProviders");
    }
    return !!asset ? /* @__PURE__ */ jsx(Container, __spreadProps(__spreadValues({}, contentMotionProps), { isPresent, children: assets.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs(CpslRow, { children: [
        /* @__PURE__ */ jsx(HeaderSelectContainer, { children: assets.length > 1 ? /* @__PURE__ */ jsxs(
          HeaderSelect,
          {
            selectedValue: asset,
            onCpslSelectValueChange: (e) => {
              setAsset(e.detail);
            },
            showFormattedSelectedItem: true,
            placeholder: "Choose asset...",
            anchorElId: "inputContainer",
            $width: 160,
            autoWidth: true,
            selectedItemVariant: "bodyXS",
            children: [
              asset && /* @__PURE__ */ jsx(AssetPill, { asset, slot: "selected-item" }),
              assets.map((a) => /* @__PURE__ */ jsx(HeaderSelectItem, { slot: "items", value: a, children: /* @__PURE__ */ jsx(AssetPill, { gap: "8px", asset: a }) }, a))
            ]
          }
        ) : /* @__PURE__ */ jsx(AssetPill, { asset, fix: true }) }),
        /* @__PURE__ */ jsx(HeaderSelectContainer, { children: narrowedNetworks.length > 1 ? /* @__PURE__ */ jsxs(
          HeaderSelect,
          {
            selectedValue: network,
            onCpslSelectValueChange: (e) => {
              setNetwork(e.detail);
            },
            showFormattedSelectedItem: true,
            placeholder: "Choose network...",
            $width: 160,
            autoWidth: true,
            selectedItemVariant: "bodyXS",
            children: [
              network && /* @__PURE__ */ jsx(NetworkPill, { network, start: NetworkLabel, slot: "selected-item" }),
              narrowedNetworks.map((n) => /* @__PURE__ */ jsx(HeaderSelectItem, { slot: "items", value: n, children: /* @__PURE__ */ jsx(NetworkPill, { gap: "8px", network: n }) }, n))
            ]
          }
        ) : network ? /* @__PURE__ */ jsx(NetworkPill, { network, start: NetworkLabel, fix: true }) : null })
      ] }),
      /* @__PURE__ */ jsxs(CpslRow, { col: true, gap: "16px", children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            value: value || "",
            fitContent: true,
            autoselect: true,
            onKeyDown: (e) => {
              if (!/^(\d|\.)$/.test(e.key) && !["Delete", "Backspace", "Tab", "Shift"].includes(e.key)) {
                e.preventDefault();
              }
            },
            onChange: (e) => {
              var _a;
              const numericValue = (((_a = e.currentTarget) == null ? void 0 : _a.value) || "").replace(/[^0-9.]/g, "");
              if (numericValue !== "") {
                const formattedValue = parseFloat(numericValue).toFixed(2);
                setValue(formattedValue);
              } else {
                setValue(null);
              }
            },
            onBlur: (e) => {
              const numericValue = (e.currentTarget.value || "").replace(/[^0-9.]/g, "");
              if (numericValue === "") {
                setValue(null);
              } else {
                setValue(parseFloat(numericValue).toFixed(2));
              }
            },
            placeholder: "0",
            children: /* @__PURE__ */ jsx(CurrencySign, { slot: "start", children: "$" })
          },
          value || ""
        ),
        /* @__PURE__ */ jsx(CpslRow, { style: { width: "100%" }, children: ["25", "50", "100"].map((quantity) => {
          return /* @__PURE__ */ jsxs(PresetButton, { fullWidth: true, variant: "secondary", onClick: () => setValue(`${quantity}.00`), children: [
            "$",
            quantity
          ] }, quantity);
        }) })
      ] }),
      /* @__PURE__ */ jsxs(
        CpslButton,
        {
          fullWidth: true,
          disabled: value === "",
          onClick: () => {
            setFiatQuantity(value);
            setOnRampStep(OnRampStep.PROVIDER);
          },
          children: [
            "Continue",
            /* @__PURE__ */ jsx(CpslIcon, { icon: "arrow" })
          ]
        }
      )
    ] }) }), "quantity") : /* @__PURE__ */ jsx(AssetContainer, __spreadProps(__spreadValues({}, contentMotionProps), { isPresent, children: /* @__PURE__ */ jsx(AddFundsAsset, {}) }), "asset");
  }, [asset, network, assets, narrowedNetworks, isProviderAllowed, isPresent, value]);
  useEffect(() => {
    if (!network || !narrowedNetworks.includes(network)) {
      setNetwork(narrowedNetworks[0]);
    }
  }, [narrowedNetworks, network, tab]);
  useEffect(() => {
    if (!!asset && !assets.includes(asset)) {
      setAsset(assets[0]);
    }
  }, [assets, asset, tab]);
  return /* @__PURE__ */ jsx(AnimatePresence, { mode: "wait", children: content });
}
const Container = styled(motion.div)`
  width: 100%;
  height: 100%;
  align-self: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  position: relative;
  height: 320px;
  ${({ isPresent }) => isPresent ? "transform: none !important;" : ""};

  & > * {
    width: 100%;
  }
`;
const AssetContainer = styled(Container)`
  gap: 8px;
`;
const CurrencySign = styled.div`
  font-size: 72px;
  color: var(--cpsl-color-text-primary);
  position: relative;
  right: -16px;
`;
const PresetButton = styled(CpslButton)`
  --button-color: var(--cpsl-color-text-contrast);
  --button-font-size: 24px;
  --button-secondary-background-color: var(--cpsl-color-background-8);
  --button-secondary-border-color: var(--cpsl-color-background-8);
  --button-secondary-hover-color: var(--cpsl-color-text-contrast);
  --button-secondary-hover-background-color: var(--cpsl-color-background-16);
  --button-secondary-hover-border-color: var(--cpsl-color-background-16);
  flex: 1;
`;
const Input = styled(CpslInput)`
  --container-background-color: transparent;
  --container-height: 90px;
  --container-border-width: 0;
  --input-background-color: transparent;
  --input-font-size: 72px;
  --input-text-align: center;
  --input-width: 100%;
`;
export {
  AddFundsSettings
};
